import type { TFunction } from 'i18next'
import orderBy from 'lodash/orderBy'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import type { Invoice } from '@features/invoices/types/invoices/invoice'
import type { RelatedDocument } from '@features/invoices/types/relatedDocument'
import { getRelatedDocumentFromRelatedInvoice } from '@features/invoices/utils/depositInvoices/getRelatedDocumentFromRelatedInvoice'
import { isDocumentTypeCreditNote } from '@features/invoices/utils/isDocumentTypeCreditNote'

interface GetInvoiceRelatedDocumentsProps {
  invoice?: Invoice
  t: TFunction
}

export const getInvoiceRelatedDocuments = ({ invoice, t }: GetInvoiceRelatedDocumentsProps): RelatedDocument[] => {
  if (!invoice) {
    return []
  }

  const relatedDocuments: RelatedDocument[] = []

  const isCreditNote = isDocumentTypeCreditNote(invoice.documentType)

  if (isCreditNote && invoice.invoiceId) {
    relatedDocuments.push({
      documentId: invoice.invoiceId,
      documentName: t('external_invoices.view.sidebar.details_card.invoice', { documentNumber: invoice.invoiceNumber }),
      documentType: InvoiceDocumentType.Invoice,
    })
  }

  if (invoice.referredCreditnoteId) {
    relatedDocuments.push({
      documentId: invoice.referredCreditnoteId,
      documentName: t('external_invoices.view.sidebar.details_card.credit_note', {
        documentNumber: invoice.creditNoteNumber,
      }),
      documentType: InvoiceDocumentType.CreditNote,
    })
  }

  if (!isCreditNote && invoice.relatedInvoices) {
    const relatedInvoiceDocuments = orderBy(
      invoice.relatedInvoices.filter(({ invoiceId }) => invoiceId !== invoice.id),
      [(relatedInvoice) => relatedInvoice.invoiceId ?? relatedInvoice.invoiceDraftId],
      ['desc'],
    ).map((relatedInvoice) => getRelatedDocumentFromRelatedInvoice(relatedInvoice, t))
    relatedDocuments.push(...relatedInvoiceDocuments)
  }

  if (invoice.estimateReference) {
    relatedDocuments.push({
      documentId: String(invoice.estimateReference.estimateId),
      documentName: t('external_invoices.view.sidebar.details_card.estimate', {
        documentNumber: invoice.estimateReference.estimateNumber,
      }),
      documentType: InvoiceDocumentType.Quote,
    })
  } else if (invoice.approvedEstimateId) {
    relatedDocuments.push({
      documentId: String(invoice.approvedEstimateId),
      documentName: t('quote'),
      documentType: InvoiceDocumentType.Quote,
    })
  }

  return relatedDocuments
}
